import { useMutation, useQueryClient } from '@tanstack/react-query';

import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analysisKeys } from './queryKeys';
import type { AnalysisDefinition, AnalysisVersion } from './types';

interface SaveAnalysisVersionInput {
  projectId: string;
  analysisProfileId: string;
  /** The currently-loaded version id — PATCH target. The backend appends a new
   *  version row and re-points the profile at it. */
  analysisVersionId: string;
  definition: AnalysisDefinition;
}

export const useSaveAnalysisVersionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      analysisProfileId,
      analysisVersionId,
      definition,
    }: SaveAnalysisVersionInput) =>
      authFetch<ObjectResponse<AnalysisVersion>>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}` +
          `/analysis-version/${encodeURIComponent(analysisVersionId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ jsonValue: definition }),
        }
      ),
    onSuccess: (envelope, variables) => {
      // The new version is already in hand, so the route that follows it renders
      // without a second round trip.
      queryClient.setQueryData(analysisKeys.version(envelope.data.analysisVersionId), envelope);
      // The profile's `latestAnalysisVersionId` and `modifiedBy` changed.
      queryClient.invalidateQueries({
        queryKey: analysisKeys.profile(variables.analysisProfileId),
      });
      // The results response flags the last run stale against the latest saved
      // version, so a new version flips it without any other query changing.
      queryClient.invalidateQueries({
        queryKey: analysisKeys.results(variables.analysisProfileId),
      });
    },
  });
};
